import { useMemo, useState } from 'react';
import { useNotify, useShowContext } from 'react-admin';
import Papa from 'papaparse';
import { API_URL } from 'shared/constants/api-url';
import { httpClient } from 'shared/services/http-client';
import { API_BASE_URL_ADMIN } from 'shared/config';
import { ERROR_MSG } from "../../../../../shared/constants/error-msg";
import { isForbiddenError, isValidationError } from "../../../../../shared/utils/errors";
import { AdminCompanyResponseDto, AdminCompanyResponseDtoTypeEnum } from 'shared/api';
import { useFieldDescriptorGet } from './hooks/useFieldDescriptorGet';
import { useDataExtract } from './hooks/useDataExtract';
import { handleValidationError } from 'shared/utils/handle-validation-error';

const MAX_FILE_SIZE = 100 * 1024 * 1024;
const MAX_ROWS = 1000;

const ONE_YEAR_FROM_NOW = new Date();
ONE_YEAR_FROM_NOW.setFullYear(ONE_YEAR_FROM_NOW.getFullYear() + 1);

const UploadPatients = () => {
  const { record: company } = useShowContext<AdminCompanyResponseDto>();
  const { requiredFields, fieldDescriptors } = useFieldDescriptorGet(company?.type);
  const [extractData] = useDataExtract();
  
  const notify = useNotify();
  const [isLoading, setIsLoading] = useState(false);

  const companyId = company?.id;

  const clearFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.target.value = '';
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!companyId || !company) {
      notify('id компании не определен', { type: 'error' });
      return clearFile(event);
    }

    const file = event.target.files?.[0];
    
    if (!file) return;

    if (file.size > MAX_FILE_SIZE) {
      notify('Размер файла превышает 100 МБ', { type: 'error' });
      return clearFile(event);
    }

    setIsLoading(true);

    try {
      const results = await new Promise<string[][]>((resolve, reject) => {
        Papa.parse(file, {
          complete: (results) => {
            if (results.data.length > MAX_ROWS) {
              reject('Превышено максимальное количество строк (1000)');
              return;
            }
            resolve(results.data as string[][]);
          },
          error: (error) => reject(error.message),
        });
      });

      const extractionResult = extractData(results, fieldDescriptors);

      if (!extractionResult.success) {
        notify(extractionResult.message, { type: 'error' });
        return clearFile(event);
      }

      const data = extractionResult.data;

      const emails = data.map(item => item.email.toLowerCase());
      const duplicateEmails = emails.filter((email, index) => emails.indexOf(email) !== index);
      
      if (duplicateEmails.length > 0) {
        notify(`Найдены дублирующиеся email адреса: ${duplicateEmails.join(', ')}`, { type: 'error' });
        return clearFile(event);
      }

      const extraValidationErrors = data.reduce<string[]>((acc, { balanceEndWith, balanceStartWith }, index) => {
        if (!balanceEndWith || !balanceStartWith) {
          return acc;
        }
        
        if (new Date(balanceStartWith) > new Date(balanceEndWith)) {
          acc.push(`balanceStartWith должен быть меньше balanceEndWith: Строка ${index + 2}`);
        }

        return acc;
      }, []);

      if (extraValidationErrors.length > 0) {
        notify(extraValidationErrors.join('\n'), { type: 'error' });
        return clearFile(event);
      }

      await httpClient(`${API_BASE_URL_ADMIN}${API_URL.uploadCompanyPatients(companyId)}`, {
        method: 'POST',
        body: JSON.stringify({ patients: data }),
      });

      notify('Данные успешно загружены', { type: 'success' });
      clearFile(event);
    } catch (error: any) {
      let msg = error instanceof Error ? error.message : 'Произошла ошибка при обработке файла';

      if (isValidationError(error)) {
        const message = handleValidationError(error.body.data);

        msg = message || msg;
      }

      if (isForbiddenError(error)) {
        msg = ERROR_MSG.forbidden
      }

      notify(msg, { type: 'error' });

      return clearFile(event);
    } finally {
      setIsLoading(false);
    }
  };

  const uploadRules = useMemo(() => {
    if (!company) {
      return null;
    }

    if (company.type === AdminCompanyResponseDtoTypeEnum.Balance) {
      return (
        <ul>
          <li>Максимальный размер файла: 100 МБ</li>
          <li>Максимальное количество строк: 1000</li>
          <li>Колонка balance: максимальное значение - 10, минимальное - 0</li>
          <li>Колонка balanceStartWith (формат Y-m-d): должна содержать значение даты, не более чем на год вперед (Например: 2025-06-20) </li>
          <li>Колонка balanceEndWith (формат Y-m-d): должна быть не меньше текущей даты и больше, чем значение даты в balanceStartWith (Например: 2025-07-20)</li>
          <li>Обязательные колонки: email, role, balance, balanceStartWith, balanceEndWith</li>
          <li>Опциональные колонки: group</li>
        </ul>
      );
    }

    if (company.type === AdminCompanyResponseDtoTypeEnum.Promocode) {
      return (
        <ul>
          <li>Максимальный размер файла: 100 МБ</li>
          <li>Максимальное количество строк: 1000</li>
          <li>Колонка quantity: максимальное значение - 10, минимальное - 0</li>
          <li> В файле должна содержаться одна из следующих колонок:
            <ul>
              <li>Колонка amount: максимальное значение - 50000, минимальное - 100</li>
              <li>Колонка percentage: максимальное значение - 100, минимальное - 5</li>
            </ul>
          </li>
          <li>Колонка expirationDate (формат Y-m-d): должна содержать значение даты, не более чем на год вперед (Например: 2025-06-20) </li>
          <li>Опциональные колонки: group</li>
        </ul>
      );
    }

    return null;
  }, [company]);

  if (!company) {
    return null;
  }

  return (
    <div style={{ padding: '20px' }}>
      <h3>Загрузка сотрудников</h3>
      <p>Загрузите CSV файл со списком сотрудников. Файл должен содержать колонки: {requiredFields.join(', ')}.</p>
      <p>Ограничения:</p>
      {uploadRules}
      <input
        type="file"
        accept=".csv"
        onChange={handleFileUpload}
        disabled={isLoading}
        style={{ marginTop: '20px' }}
      />
      {isLoading && <div>Загрузка...</div>}
    </div>
  );
};

export default UploadPatients;
